import * as Yup from 'yup'
import { isValidPhoneNumber } from 'react-phone-number-input'

import type { IOption } from 'common/interfaces/IOption'
import type { Patient, PatientContactPayload } from 'features/Procedure/state/procedure-api.type'

const MAX_TEXT_LENGTH = 255
const MAX_TEXT_MESSAGE = 'Maximum 255 characters'

export const patientContactSchema = Yup.object({
  dob: Yup.string(),
  address: Yup.string().max(MAX_TEXT_LENGTH, MAX_TEXT_MESSAGE),
  city: Yup.string().max(MAX_TEXT_LENGTH, MAX_TEXT_MESSAGE),
  zip_code: Yup.string().max(MAX_TEXT_LENGTH, MAX_TEXT_MESSAGE),
  state: Yup.string().max(MAX_TEXT_LENGTH, MAX_TEXT_MESSAGE),
  country: Yup.string().max(MAX_TEXT_LENGTH, MAX_TEXT_MESSAGE),
  billing_note: Yup.string(),
  general_note: Yup.string(),
  email: Yup.string().max(MAX_TEXT_LENGTH, MAX_TEXT_MESSAGE).email('Please enter a valid email'),
  phone: Yup.string().test(
    'is-valid-phone',
    'Please enter a valid phone number',
    (value) => !value || isValidPhoneNumber(value),
  ),
  referral_source: Yup.string(),
})

export type PatientContactFormValues = Yup.InferType<typeof patientContactSchema>

export const US_COUNTRY_CODE = 'US'
export const DEFAULT_STATE_CODE = 'CA'

// Values returned by the API that should resolve to the US country code.
// An empty/missing country also defaults to the US.
const US_COUNTRY_ALIASES = new Set(['', 'US', 'USA', 'UNITED STATES', 'UNITED STATES OF AMERICA'])

export const normalizeCountry = (country?: string | null): string => {
  const value = (country ?? '').trim()
  return US_COUNTRY_ALIASES.has(value.toUpperCase()) ? US_COUNTRY_CODE : value
}

export const normalizeState = (state: string | null | undefined, country: string): string => {
  const value = (state ?? '').trim()
  if (value) {
    return value
  }
  return country === US_COUNTRY_CODE ? DEFAULT_STATE_CODE : value
}

export const buildInitialContactValues = (patient: Patient | null): PatientContactFormValues => {
  const country = normalizeCountry(patient?.country)
  const state = normalizeState(patient?.state, country)

  return {
    dob: patient?.dob ?? '',
    address: patient?.address ?? '',
    city: patient?.city ?? '',
    zip_code: patient?.zip_code ?? '',
    state,
    country,
    billing_note: patient?.billing_note ?? '',
    general_note: patient?.general_note ?? '',
    email: patient?.email ?? '',
    phone: patient?.phone ?? '',
    referral_source: patient?.referral_source ?? '',
  }
}

export type ContactSelectOption = {
  value: string
  label: string
}

export const toCountrySelectOptions = (countries?: IOption[]): ContactSelectOption[] =>
  (countries ?? []).map((country) => ({
    value: String(country.name).split(',')[0].trim(),
    label: country.name,
  }))

export const toStateSelectOptions = (states?: IOption[]): ContactSelectOption[] =>
  (states ?? []).map((state) => {
    const code = state.short_name ?? String(state.name)
    return { value: code, label: code }
  })

export const toReferralSourceOptions = (
  referralSources?: Record<string, string>,
): ContactSelectOption[] =>
  Object.entries(referralSources ?? {}).map(([label, value]) => ({ value, label }))

export const referralSourceExists = (
  referralSources: Record<string, string> | undefined,
  title: string,
): boolean =>
  Object.entries(referralSources ?? {}).some(([label, value]) => label === title || value === title)

export const toContactPayload = (values: PatientContactFormValues): PatientContactPayload => ({
  dob: values.dob ?? '',
  address: values.address ?? '',
  city: values.city ?? '',
  zip_code: values.zip_code ?? '',
  state: values.state ?? '',
  country: values.country ?? '',
  billing_note: values.billing_note ?? '',
  general_note: values.general_note ?? '',
  email: values.email ?? '',
  phone: values.phone ?? '',
  referral_source: values.referral_source ?? '',
})
